import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AppFooter } from '../components/AppFooter';
import { getErrorMessage } from '../utils/getErrorMessage';
import '../styles/LoginPage.css';
import logo from '../assets/logo.png';

interface PasswordStrength {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  // Calculate password strength
  const getPasswordStrength = (password: string): PasswordStrength => {
    return {
      minLength: password.length >= 10,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?/`~]/.test(password)
    };
  };

  const strength = getPasswordStrength(formData.password);
  const isStrongPassword = Object.values(strength).every(Boolean);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (!isStrongPassword) {
      setError('Password does not meet strength requirements');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token,
            password: formData.password
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Failed to reset password');
      }

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={logo} alt="Belafonte TACOLCY Center" className="login-logo" />
            <h2 className="login-title">Password Reset Successful!</h2>
          </div>

          <div className="login-form">
            <div style={{
              padding: '1.5rem',
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <p style={{ color: '#155724', marginBottom: '1rem', fontSize: '1.1rem' }}>
                ✓ Your password has been successfully reset.
              </p>
              <p style={{ color: '#155724', marginBottom: 0 }}>
                Redirecting you to the login page...
              </p>
            </div>

            <Link to="/login">
              <button className="button-primary login-button">
                Go to Login
              </button>
            </Link>
          </div>

          <AppFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={logo} alt="Belafonte TACOLCY Center" className="login-logo" />
          <h2 className="login-title">Reset Your Password</h2>
          <p className="login-subtitle">Enter your new password below.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          {/* New Password */}
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                disabled={!token}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                disabled={!token}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          {/* Password Strength Indicator */}
          {formData.password && (
            <div className="password-strength" style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Password Requirements:</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ color: strength.minLength ? 'green' : 'red' }}>
                  {strength.minLength ? '✓' : '✗'} At least 10 characters
                </li>
                <li style={{ color: strength.hasUppercase ? 'green' : 'red' }}>
                  {strength.hasUppercase ? '✓' : '✗'} Contains uppercase letter (A-Z)
                </li>
                <li style={{ color: strength.hasLowercase ? 'green' : 'red' }}>
                  {strength.hasLowercase ? '✓' : '✗'} Contains lowercase letter (a-z)
                </li>
                <li style={{ color: strength.hasNumber ? 'green' : 'red' }}>
                  {strength.hasNumber ? '✓' : '✗'} Contains number (0-9)
                </li>
                <li style={{ color: strength.hasSpecial ? 'green' : 'red' }}>
                  {strength.hasSpecial ? '✓' : '✗'} Contains special character (!@#$%^&*, etc.)
                </li>
              </ul>
            </div>
          )}

          <button
            type="submit"
            className="button-primary login-button"
            disabled={loading || !isStrongPassword || !token}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <Link to="/login" style={{ color: '#5B2C86', textDecoration: 'none', fontSize: '0.95rem' }}>
              ← Back to Login
            </Link>
          </div>
        </form>

        <AppFooter />
      </div>
    </div>
  );
}
